import {useCallback, useMemo, useRef, useState} from 'react';
import {NovaTestTask} from '../shared/novaTaskVariables';
import {NovaGroundingAccount, NovaServerGroundingAccount, toGroundingAccount} from '../shared/novaGroundingAccount';

export const NovaDryRunState = {
	NoTask: 'NO_TASK',
	Ready: 'READY',
	Running: 'RUNNING',
	Result: 'RESULT',
	Error: 'ERROR',
} as const;
export type NovaDryRunState = (typeof NovaDryRunState)[keyof typeof NovaDryRunState];

export interface NovaDryRunTurn {
	// Null on the opening turn; the author's text on a follow-up.
	message: string | null;
	output: string;
	ranAt: string;
	// The account this turn's own run recorded — a follow-up is more per-run input, so its grounding
	// is its own. Null where the run recorded none, which is not the same as grounding in nothing.
	grounding: NovaGroundingAccount | null;
	// The model id the saved run record holds. An id rather than a name: the record persists only
	// the id, and the display name is resolved against the model list the page already holds.
	modelId: string | null;
}

// One run as it was asked for, so a failure can be repeated exactly.
interface NovaDryRunAttempt {
	message: string | null;
	replaceTurns: boolean;
	// The thread this attempt was sent to. Held on the attempt rather than read back off state at
	// retry time for the same reason `replaceTurns` is: a retry has to repeat the ask, and the ask
	// included which conversation it belonged to.
	threadId: string | null;
}

export interface NovaRunAgentArguments {
	taskId: string;
	message: string | null;
	// The thread to continue, or null to start one. Null on every opening turn: nothing derives a
	// thread from the agent and task alone — two runs against one task are not one conversation —
	// so a thread is only ever continued by carrying back the id a prior turn returned.
	threadId: string | null;
}

export interface NovaRunAgentResult {
	output: string;
	ranAt: string;
	// The saved run record, read back after the write completed. Absent on a result that predates
	// the record being selected at all.
	//
	// `threadId` is the thread this turn belongs to, minted server-side on an opening turn. Null
	// where the record named none, which simply means the next turn opens a fresh thread rather than
	// continuing this one.
	run?: {
		modelDisplayName: string | null;
		threadId: string | null;
		grounding?: NovaServerGroundingAccount | null;
	} | null;
}

// Injected rather than imported so this hook has no Relay dependency and can be tested with a plain
// mock — the same reason NovaStudioShell takes its mutations from the page above it.
export type NovaRunAgent = (args: NovaRunAgentArguments) => Promise<NovaRunAgentResult>;

export interface UseNovaDryRunOptions {
	runAgent: NovaRunAgent;
}

export interface NovaDryRunController {
	state: NovaDryRunState;
	selectedTask: NovaTestTask | null;
	turns: NovaDryRunTurn[];
	canRun: boolean;
	selectTask: (task: NovaTestTask) => void;
	clearTask: () => void;
	run: () => Promise<void>;
	followUp: (message: string) => Promise<void>;
	retry: () => Promise<void>;
	clearConversation: () => void;
}

// The run lifecycle for one agent: which task is selected, the turns so far, and the actions that
// move between them. Nothing here writes to the task — the injected `runAgent` is a dry run by
// construction, and this hook never sees a write path at all.
export function useNovaDryRun(options: UseNovaDryRunOptions): NovaDryRunController {
	const {runAgent} = options;
	const [selectedTask, setSelectedTask] = useState<NovaTestTask | null>(null);
	const [turns, setTurns] = useState<NovaDryRunTurn[]>([]);
	const [isRunning, setIsRunning] = useState(false);
	const [hasFailed, setHasFailed] = useState(false);
	// The attempt Retry replays. Held rather than reconstructed because a failed follow-up is not the
	// opening turn: retrying it has to re-send that message and append to the conversation, not start
	// a fresh one over the top of the turns already on screen.
	const [lastAttempt, setLastAttempt] = useState<NovaDryRunAttempt | null>(null);
	// The thread the turns on screen belong to, as the server named it. Carried back on a follow-up so
	// the model is replayed its own prior turns; without it every follow-up would re-ground the whole
	// definition and ask its question of a model that has never seen the conversation.
	const [threadId, setThreadId] = useState<string | null>(null);
	// Bumped whenever the conversation on screen is replaced — by a reset or by another run. A run
	// captures this before it awaits and drops its own result if the value has moved on, because
	// neither the picker nor Clear conversation is disabled while a run is in flight, and nothing can
	// recall a request already sent. Without it a response that outlived its conversation writes its
	// turn and installs its thread over the top of the reset, and the next follow-up is sent to a
	// thread about a task the author is no longer looking at.
	const generationRef = useRef(0);

	const state = useMemo((): NovaDryRunState => {
		if (isRunning) {
			return NovaDryRunState.Running;
		}
		if (hasFailed) {
			return NovaDryRunState.Error;
		}
		if (selectedTask === null) {
			return NovaDryRunState.NoTask;
		}
		return turns.length > 0 ? NovaDryRunState.Result : NovaDryRunState.Ready;
	}, [isRunning, hasFailed, selectedTask, turns.length]);

	// `replaceTurns` distinguishes Run from a follow-up: Run starts the conversation over, a follow-up
	// extends it. Both go through one path so the failure handling cannot diverge. `continuing` is the
	// thread to send it to, decided by the caller rather than read off state here, so a retry can send
	// the thread its failed attempt was actually addressed to.
	const execute = useCallback(
		async (message: string | null, replaceTurns: boolean, continuing: string | null): Promise<void> => {
			if (selectedTask === null) {
				return;
			}
			const generation = generationRef.current + 1;
			generationRef.current = generation;
			setIsRunning(true);
			setHasFailed(false);
			setLastAttempt({message, replaceTurns, threadId: continuing});
			const isCurrent = () => generationRef.current === generation;
			try {
				const result = await runAgent({taskId: selectedTask.taskId, message, threadId: continuing});
				if (!isCurrent()) {
					return;
				}
				const turn: NovaDryRunTurn = {
					message,
					output: result.output,
					ranAt: result.ranAt,
					grounding: toGroundingAccount(result.run?.grounding),
					modelId: result.run?.modelDisplayName ?? null,
				};
				setTurns(previous => (replaceTurns ? [turn] : [...previous, turn]));
				// Falls back to the thread this turn was continuing, not to null. Both the record and the
				// field are nullable, so a read-back the resolver could not serve would otherwise discard
				// a thread the server had just appended this very turn to — and the next follow-up would
				// open a third conversation the model has seen none of, with nothing on screen changing
				// to say so. On an opening turn `continuing` is null, so that case is unaffected.
				setThreadId(result.run?.threadId ?? continuing);
			} catch (error) {
				// A failed run leaves no turn at all: a half-rendered card would read as an answer. A
				// failure this late is reported only if its conversation is still the one on screen —
				// otherwise the error names a run whose subject the author has already cleared.
				if (isCurrent()) {
					setHasFailed(true);
				}
			} finally {
				// The resets clear this themselves, so a superseded run must not clear it on behalf of
				// whatever replaced it.
				if (isCurrent()) {
					setIsRunning(false);
				}
			}
		},
		[selectedTask, runAgent]
	);

	// Abandons whatever is in flight along with the turns: the generation moves on, so a response
	// still on its way lands on nothing. The request itself cannot be recalled — it will finish, and
	// its record is written server-side either way — but the section stops waiting for it.
	const abandon = useCallback(() => {
		generationRef.current += 1;
		setIsRunning(false);
		setHasFailed(false);
		setLastAttempt(null);
		setThreadId(null);
	}, []);

	const selectTask = useCallback(
		(task: NovaTestTask) => {
			// A conversation is about one task, so pointing the agent somewhere else starts fresh rather
			// than carrying turns across a change of subject.
			abandon();
			setSelectedTask(task);
			setTurns([]);
		},
		[abandon]
	);

	const clearTask = useCallback(() => {
		abandon();
		setSelectedTask(null);
		setTurns([]);
	}, [abandon]);

	// Run always opens a fresh thread. It replaces the turns on screen, so continuing the thread they
	// belonged to would leave the model replaying a conversation the author can no longer see.
	const run = useCallback(() => execute(null, true, null), [execute]);
	const followUp = useCallback((message: string) => execute(message, false, threadId), [execute, threadId]);
	// Retry repeats the attempt that failed, whichever it was. A failed follow-up leaves the earlier
	// turns intact and still on screen, so replaying it as a fresh opening turn would discard the
	// conversation and never re-send the message the author actually lost.
	const retry = useCallback(
		() =>
			lastAttempt === null
				? Promise.resolve()
				: execute(lastAttempt.message, lastAttempt.replaceTurns, lastAttempt.threadId),
		[execute, lastAttempt]
	);

	const clearConversation = useCallback(() => {
		abandon();
		setTurns([]);
	}, [abandon]);

	return {
		state,
		selectedTask,
		turns,
		canRun: selectedTask !== null && !isRunning,
		selectTask,
		clearTask,
		run,
		followUp,
		retry,
		clearConversation,
	};
}
